import logo_tipddy from "./assets/logo-tipddy.png";
import "./index.css";
import { useTheme } from "../../contexts/ThemeContext";
import { useConfig } from "../../contexts/config";
import React from "react";
import { readableColor } from "polished";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";

export const Footer = () => {
  const { config } = useConfig();
  const theme = useTheme();
  const [contrastColor, setContrastColor] = React.useState("#fff");

  React.useEffect(() => {
    if (theme && theme.colors.backgroundColorFooter) {
      setContrastColor(
        readableColor(theme.colors.backgroundColorFooter, "#1a1a1a", "#f2f2f2", false)
      );
    }
    // eslint-disable-next-line
  }, [theme]);

  const socialLinks = [
    {
      href: "https://wa.me/34611452914",
      icon: <WhatsAppIcon style={{ fontSize: "2.4rem", color: contrastColor }} />,
      label: "WhatsApp",
    },
    {
      href: "mailto:academia8islas@gmail.com",
      icon: <EmailIcon style={{ fontSize: "2.4rem", color: contrastColor }} />,
      label: "Email",
    },
    {
      href: "https://www.facebook.com/academia8islas",
      icon: <FacebookIcon style={{ fontSize: "2.4rem", color: contrastColor }} />,
      label: "Facebook",
    },
    {
      href: "https://www.instagram.com/academia8islas/",
      icon: <InstagramIcon style={{ fontSize: "2.4rem", color: contrastColor }} />,
      label: "Instagram",
    },
  ];

  const interestLinks = [
    { label: "Inicio", href: "https://academia8islas.com/" },
    { label: "Cursos", href: "https://academia8islas.com/cursos/" },
    { label: "FAQ", href: "https://academia8islas.com/cursos/#faq" },
    { label: "Contacto", href: "https://academia8islas.com/contacto/" },
  ];

  const alumniLinks = [
    { label: "Acceso campus", href: "https://academia8islas.com/acceso-campus/" },
    { label: "Inicia sesión", href: "https://academia8islas.com/mi-cuenta/" },
    { label: "Mi perfil", href: "https://academia8islas.com/mi-cuenta/" },
    { label: "Tribu", href: "https://academia8islas.com/" },
  ];

  const legalLinks = [
    { label: "Aviso legal", href: "https://academia8islas.com/aviso-legal/" },
    { label: "Política de privacidad", href: "https://academia8islas.com/politica-de-privacidad/" },
    { label: "Política de cookies", href: "https://academia8islas.com/politica-de-cookies/" },
    { label: "Condiciones", href: "https://academia8islas.com/condiciones-de-venta/" },
  ];

  return (
    <footer style={{ backgroundColor: theme.colors.backgroundColorFooter }}>
      {/* ── MOBILE: Minimalist App Footer (< 768px) ── */}
      <div className="footer-mobile">
        {/* Main section: Social + Legal + Copyright */}
        <div className="flex flex-col items-center text-center gap-3.5 py-6 px-4">
          {/* Social / Contact links */}
          <div className="flex justify-center items-center gap-3">
            {socialLinks.map((s, i) => (
              <a
                key={i}
                href={s.href}
                target={s.href.startsWith("http") ? "_blank" : undefined}
                rel={s.href.startsWith("http") ? "noopener noreferrer" : undefined}
                aria-label={s.label}
                className="w-10 h-10 flex items-center justify-center hover:opacity-75 transition-opacity"
              >
                {React.cloneElement(s.icon, { style: { fontSize: "24px", color: contrastColor } })}
              </a>
            ))}
          </div>

          {/* Legal links inline */}
          <div
            className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1"
            style={{ fontSize: "12px", color: contrastColor, opacity: 0.65 }}
          >
            {legalLinks.map((item, idx) => (
              <React.Fragment key={item.label}>
                {idx > 0 && <span style={{ opacity: 0.4 }}>·</span>}
                <a
                  href={item.href}
                  target={item.href.startsWith("http") ? "_blank" : undefined}
                  rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="hover:underline"
                  style={{ color: contrastColor }}
                >
                  {item.label}
                </a>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Sub-barra final: Powered by Tipddy | 2026 Academia 8 Islas */}
        <div
          className="w-full py-2.5 px-4 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            borderTop: `1px solid ${contrastColor}14`,
          }}
        >
          <div className="flex items-center gap-1.5">
            <span
              style={{
                fontSize: "11px",
                color: "#94a3b8",
                letterSpacing: "0.03em",
                fontWeight: 500,
              }}
            >
              Powered by
            </span>
            <img
              src={logo_tipddy}
              alt="Tipddy Logo"
              className="h-[14px] w-auto object-contain opacity-85"
            />
            <span
              style={{
                fontSize: "12px",
                color: "#e2e8f0",
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              Tipddy
            </span>
          </div>

          <span style={{ color: "#94a3b8", opacity: 0.4 }}>|</span>

          <span
            style={{
              fontSize: "11px",
              color: contrastColor,
              opacity: 0.55,
              whiteSpace: "nowrap",
            }}
          >
            2026 Academia 8 Islas
          </span>
        </div>
      </div>

      {/* ── TABLET / DESKTOP: Full structured footer (>= 768px) ── */}
      <div className="footer-desktop">
        <div className="max-w-[1400px] mx-auto px-6 tablet:px-10 laptop:px-16 py-8">
          <div className="grid grid-cols-2 laptop:grid-cols-4 gap-8 laptop:gap-12 items-start">

            {/* Col 1: Logo + tagline + social */}
            <div className="flex flex-col gap-4">
              <img
                src={
                  config.theme &&
                  (theme && theme.mode === "light"
                    ? config.theme.logo
                    : config.theme.theme_logo_dark)
                }
                alt="Logo"
                className="max-w-[200px] max-h-[65px] object-contain"
              />
              {config.description && (
                <p className="text-sm leading-relaxed" style={{ color: contrastColor, opacity: 0.75 }}>
                  {config.description}
                </p>
              )}
              <div className="flex gap-4 mt-2">
                {socialLinks.map((s, i) => (
                  <a
                    key={i}
                    href={s.href}
                    target={s.href.startsWith("http") ? "_blank" : undefined}
                    rel={s.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    aria-label={s.label}
                    className="hover:opacity-70 transition-opacity"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Col 2: Enlaces de interés */}
            <div className="flex flex-col gap-3">
              <p className="text-base font-semibold mb-2" style={{ color: contrastColor }}>
                Enlaces de interés
              </p>
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {interestLinks.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="text-sm hover:underline transition-opacity"
                      style={{ color: contrastColor, opacity: 0.75 }}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 3: Alumnos */}
            <div className="flex flex-col gap-3">
              <p className="text-base font-semibold mb-2" style={{ color: contrastColor }}>
                Alumnos
              </p>
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {alumniLinks.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="text-sm hover:underline transition-opacity"
                      style={{ color: contrastColor, opacity: 0.75 }}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 4: Legal */}
            <div className="flex flex-col gap-3">
              <p className="text-base font-semibold mb-2" style={{ color: contrastColor }}>
                Legal
              </p>
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {legalLinks.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="text-sm hover:underline transition-opacity"
                      style={{ color: contrastColor, opacity: 0.75 }}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="w-full"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            borderTop: `1px solid ${contrastColor}18`,
          }}
        >
          <div className="max-w-[1400px] mx-auto px-6 tablet:px-10 laptop:px-16 py-3.5 flex flex-row justify-between items-center gap-4 flex-wrap">
            <p className="text-sm m-0" style={{ color: contrastColor, opacity: 0.55 }}>
              © 2026 Academia 8 Islas
            </p>

            <div className="flex items-center gap-2">
              <span
                style={{
                  fontSize: "11px",
                  color: "#94a3b8",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  fontWeight: 500,
                }}
              >
                Powered by
              </span>
              <img src={logo_tipddy} alt="Tipddy Logo" className="h-[16px] w-auto object-contain opacity-85" />
              <span style={{ fontSize: "13px", color: "#e2e8f0", fontWeight: 600, letterSpacing: "0.02em" }}>
                Tipddy
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
